// src/screens/customer/customerPannel/customRequests/CustomRequestDetail.jsx

import React, { useContext, useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  ImageIcon,
  Clock,
  CheckCircle,
  XCircle,
  Tag,
  Calendar,
  Hash,
  Package,
  AlertCircle,
  X,
  ThumbsUp,
  ThumbsDown,
  ZoomIn,
  Ruler,
  FileText,
} from "lucide-react";
import "../../../../assets/css/customer/customerPannel/customrequests/CustomOrderDetail.css";
import AppContext from "../../../../context/AppContext";
import axios from "axios";

// ─────────────────────────────────────────────────────────────
// CONSTANTS — keys match backend "status" values (lowercase)
// ─────────────────────────────────────────────────────────────
const STATUS_CONFIG = {
  pending: {
    label: "Awaiting Quote",
    color: "#b45309",
    bg: "rgba(234,179,8,0.1)",
    icon: Clock,
  },
  quoted: {
    label: "Quote Received",
    color: "#2563eb",
    bg: "rgba(37,99,235,0.1)",
    icon: Tag,
  },
  accepted: {
    label: "Order Placed",
    color: "#16a34a",
    bg: "rgba(22,163,74,0.1)",
    icon: CheckCircle,
  },
  rejected: {
    label: "Rejected",
    color: "#dc2626",
    bg: "rgba(220,38,38,0.1)",
    icon: XCircle,
  },
};

// ─────────────────────────────────────────────────────────────
// IMAGE LIGHTBOX — expects an array of plain image URL strings
// ─────────────────────────────────────────────────────────────
const Lightbox = ({ images, startIndex, onClose }) => {
  const [current, setCurrent] = useState(startIndex);

  return (
    <div className="crd-lightbox-overlay" onClick={onClose}>
      <div className="crd-lightbox-box" onClick={(e) => e.stopPropagation()}>
        <button className="crd-lightbox-close" onClick={onClose}>
          <X size={18} strokeWidth={2.5} />
        </button>
        <img src={images[current]} alt="" className="crd-lightbox-img" />
        {images.length > 1 && (
          <div className="crd-lightbox-dots">
            {images.map((_, i) => (
              <button
                key={i}
                className={`crd-lightbox-dot ${i === current ? "active" : ""}`}
                onClick={() => setCurrent(i)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// RESULT POPUP (reject outcome — accept navigates straight to checkout)
// ─────────────────────────────────────────────────────────────
const ResultPopup = ({ type, onClose, onGoToOrders }) => {
  const isAccepted = type === "accept";

  return (
    <div className="crd-result-overlay">
      <div className="crd-result-popup">
        <div
          className={`crd-result-icon ${isAccepted ? "crd-result-icon--green" : "crd-result-icon--red"}`}
        >
          {isAccepted ? (
            <ThumbsUp size={30} strokeWidth={1.8} />
          ) : (
            <ThumbsDown size={30} strokeWidth={1.8} />
          )}
        </div>
        <h3 className="crd-result-title">
          {isAccepted ? "Quote Accepted!" : "Quote Declined"}
        </h3>
        <p className="crd-result-msg">
          {isAccepted
            ? "Your order has been placed. Our team will begin production and keep you updated."
            : "You've declined this quote. The request is now closed. You can submit a new request anytime."}
        </p>
        <div className="crd-result-actions">
          {isAccepted && (
            <button className="crd-btn-primary" onClick={onGoToOrders}>
              <CheckCircle size={15} strokeWidth={2.2} />
              Go to My Orders
            </button>
          )}
          <button
            className={isAccepted ? "crd-btn-secondary" : "crd-btn-primary"}
            onClick={onClose}
          >
            {isAccepted ? "Stay Here" : "Back to Requests"}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// CONFIRM POPUP
// ─────────────────────────────────────────────────────────────
const ConfirmPopup = ({ type, quotedPrice, onConfirm, onCancel }) => {
  const isAccepted = type === "accept";

  return (
    <div className="crd-result-overlay">
      <div className="crd-result-popup">
        <div
          className={`crd-result-icon ${isAccepted ? "crd-result-icon--green" : "crd-result-icon--red"}`}
        >
          <AlertCircle size={30} strokeWidth={1.8} />
        </div>
        <h3 className="crd-result-title">
          {isAccepted ? "Accept this Quote?" : "Decline this Quote?"}
        </h3>
        <p className="crd-result-msg">
          {isAccepted
            ? `You are about to accept the quoted price of PKR ${quotedPrice?.toLocaleString()}. You'll be taken to checkout to complete your order.`
            : "You are about to decline this quote. This action cannot be undone."}
        </p>
        <div className="crd-result-actions">
          <button
            className={isAccepted ? "crd-btn-primary" : "crd-btn-danger"}
            onClick={onConfirm}
          >
            {isAccepted ? "Yes, Accept" : "Yes, Decline"}
          </button>
          <button className="crd-btn-secondary" onClick={onCancel}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────
const CustomRequestDetail = () => {
  const { baseUrl, authToken, baseUrlForFile } = useContext(AppContext);
  const { id } = useParams();
  const navigate = useNavigate();

  const [req, setReq] = useState(null);
  const [quotedPrice, setQuotedPrice] = useState("");
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const [confirmType, setConfirmType] = useState(null); // "accept" | "reject"
  const [resultType, setResultType] = useState(null); // "accept" | "reject"

  useEffect(() => {
    viewDetails();
  }, [id, status]);

  const viewDetails = () => {
    axios
      .get(`${baseUrl}/customer/cart/viewCustomReqDetails`, {
        headers: { Authorization: `Bearer ${authToken}` },
        params: { id },
      })
      .then((response) => {
        console.log(response.data);
        const item = response.data.singleCustomReq;
        setReq(item);
        setStatus(item?.status); // ← status now comes from API
        setQuotedPrice(item?.quotedPrice);
        setLoading(false);
      })
      .catch((error) => {
        console.log(error);
        setLoading(false);
      });
  };

  if (loading) {
    return (
      <div
        className="crd-page"
        style={{ padding: "60px", textAlign: "center" }}
      >
        Loading...
      </div>
    );
  }

  if (!req) {
    return (
      <div
        className="crd-page"
        style={{ textAlign: "center", padding: "60px 20px" }}
      >
        <h2 style={{ color: "#D67E43" }}>Request Not Found</h2>
        <p style={{ color: "#9a7060", marginTop: "8px" }}>
          Request <strong>{id}</strong> does not exist.
        </p>
        <Link
          to="/customer/custom-requests"
          className="crd-back"
          style={{ marginTop: "20px", display: "inline-flex" }}
        >
          ← Back to Requests
        </Link>
      </div>
    );
  }

  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.pending;
  const StatusIcon = cfg.icon;

  // Plain URL strings for the lightbox (custom_cart_images is an array of objects)
  const imageUrls = (req.custom_cart_images || []).map(
    (img) => `${baseUrlForFile}/${img.image_path}`,
  );

  const updateStaus = (newStatus) => {
    const formdata = new FormData();
    formdata.append("id", id);
    formdata.append("status", newStatus);

    axios
      .post(`${baseUrl}/customer/cart/acceptRejectReq`, formdata, {
        headers: { Authorization: `Bearer ${authToken}` },
      })
      .then((response) => {
        console.log(response.data);
      })
      .catch((error) => {
        console.log(error);
      });
  };

  const handleConfirm = () => {
    if (confirmType === "accept") {
      updateStaus("accepted");
      // Hand off to Checkout — quantity is always 1 for custom requests,
      // price comes from the admin's quoted price. Phone/address are
      // collected on the checkout page itself.
      navigate("/checkout", {
        state: {
          customCheckout: true,
          customItem: {
            adcart_id: req.id,
            name: req.adproduct?.name,
            image: imageUrls[0] || null,
            price: req.quotedPrice,
            quantity: 1,
          },
        },
      });
    } else {
      setStatus("rejected");
      setResultType("reject");
      updateStaus("rejected");
    }
    setConfirmType(null);
  };

  const handleResultClose = () => {
    setResultType(null);
    if (resultType === "reject") navigate("/customer/custom-requests");
  };

  const isQuoted = status === "quoted";
  const isSettled = status === "accepted" || status === "rejected";

  return (
    <div className="crd-page">
      {/* ── Top Bar ── */}
      <div className="crd-topbar">
        <Link to="/customer/custom-requests" className="crd-back">
          <ArrowLeft size={15} strokeWidth={2.5} /> Back to Requests
        </Link>
        <div className="crd-topbar-meta">
          <span className="crd-meta-id">
            <Hash size={12} strokeWidth={2.5} /> {req.id}
          </span>
          <span className="crd-meta-date">
            <Calendar size={12} strokeWidth={2.5} />
            {new Date(req.created_at).toLocaleDateString("en-GB", {
              day: "2-digit",
              month: "short",
              year: "numeric",
            })}
          </span>
          <span
            className="crd-status-pill"
            style={{ color: cfg.color, background: cfg.bg }}
          >
            <StatusIcon size={11} strokeWidth={2.5} />
            {cfg.label}
          </span>
        </div>
      </div>

      {/* ── Main Grid ── */}
      <div className="crd-grid">
        {/* ── LEFT ── */}
        <div className="crd-col-left">
          {/* Reference Images */}
          <div className="crd-card">
            <div className="crd-card-head">
              <h2 className="crd-card-title">
                <ImageIcon size={15} strokeWidth={2.2} /> Reference Images
              </h2>
              <span className="crd-card-chip">
                {req.custom_cart_images?.length || 0} photo
                {req.custom_cart_images?.length !== 1 ? "s" : ""}
              </span>
            </div>
            {imageUrls.length > 0 ? (
              <div className="crd-images-grid">
                {imageUrls.map((src, i) => (
                  <button
                    key={i}
                    className="crd-img-thumb-btn"
                    onClick={() => setLightboxIndex(i)}
                    aria-label="View image"
                  >
                    <img src={src} alt={`ref-${i}`} className="crd-img-thumb" />
                    <div className="crd-img-overlay">
                      <ZoomIn size={18} strokeWidth={2} />
                    </div>
                  </button>
                ))}
              </div>
            ) : (
              <p className="crd-no-images">No reference images submitted.</p>
            )}
          </div>

          {/* Request Details */}
          <div className="crd-card">
            <div className="crd-card-head">
              <h2 className="crd-card-title">
                <FileText size={15} strokeWidth={2.2} /> Request Details
              </h2>
            </div>
            <div className="crd-details-list">
              <div className="crd-detail-row">
                <span className="crd-detail-label">
                  <Package size={13} strokeWidth={2} /> Furniture Type
                </span>
                <span className="crd-detail-value crd-badge-green">
                  {req.adproduct?.name}
                </span>
              </div>
              <div className="crd-detail-row">
                <span className="crd-detail-label">
                  <Ruler size={13} strokeWidth={2} /> Dimensions
                </span>
                <span className="crd-detail-value">
                  {req.adproduct?.dimensions || "—"}
                </span>
              </div>
              <div className="crd-detail-row">
                <span className="crd-detail-label">
                  <Calendar size={13} strokeWidth={2} /> Submitted
                </span>
                <span className="crd-detail-value">
                  {new Date(req.created_at).toLocaleDateString("en-GB", {
                    day: "2-digit",
                    month: "long",
                    year: "numeric",
                  })}
                </span>
              </div>
            </div>
            <div className="crd-description-box">
              <p className="crd-desc-label">Your Description</p>
              <p className="crd-desc-text">"{req.description}"</p>
            </div>
          </div>
        </div>

        {/* ── RIGHT ── */}
        <div className="crd-col-right">
          {/* Quote Card */}
          <div className="crd-card">
            <div className="crd-card-head">
              <h2 className="crd-card-title">
                <Tag size={15} strokeWidth={2.2} /> Quote from Admin
              </h2>
            </div>

            {/* Pending — no quote yet */}
            {status === "pending" && (
              <div className="crd-quote-pending">
                <Clock
                  size={32}
                  strokeWidth={1.5}
                  color="#b45309"
                  style={{ opacity: 0.6 }}
                />
                <p className="crd-quote-pending-title">Awaiting Quote</p>
                <p className="crd-quote-pending-msg">
                  Our team is reviewing your request. You'll be notified once a
                  quote is ready.
                </p>
              </div>
            )}

            {/* Admin rejected the req */}
            {status === "rejected" && !req.quotedPrice && (
              <div className="crd-quote-rejected-msg">
                <XCircle
                  size={28}
                  strokeWidth={1.5}
                  color="#dc2626"
                  style={{ opacity: 0.6 }}
                />
                <p
                  className="crd-quote-pending-title"
                  style={{ color: "#dc2626" }}
                >
                  Request Closed
                </p>
                {req.adminNote && (
                  <p className="crd-admin-note">"{req.adminNote}"</p>
                )}
              </div>
            )}

            {/* Quoted / Accepted / Customer rejected */}
            {req.quotedPrice && (
              <>
                <div className="crd-price-display">
                  <p className="crd-price-label">Quoted Price</p>
                  <p className="crd-price-value">
                    PKR {req.quotedPrice.toLocaleString()}
                  </p>
                  {req.estimatedDays && (
                    <p className="crd-est-delivery">
                      <Calendar size={12} strokeWidth={2} />
                      Estimated: {req.estimatedDays} days after confirmation
                    </p>
                  )}
                </div>

                {req.adminNote && (
                  <div className="crd-admin-note-box">
                    <p className="crd-admin-note-label">Note from Admin</p>
                    <p className="crd-admin-note-text">"{req.adminNote}"</p>
                  </div>
                )}

                {/* Accept / Reject buttons — only when status is quoted */}
                {isQuoted && (
                  <div className="crd-quote-actions">
                    <button
                      className="crd-btn-accept"
                      onClick={() => setConfirmType("accept")}
                    >
                      <ThumbsUp size={15} strokeWidth={2.2} />
                      Accept Quote
                    </button>
                    <button
                      className="crd-btn-reject"
                      onClick={() => setConfirmType("reject")}
                    >
                      <ThumbsDown size={15} strokeWidth={2.2} />
                      Decline
                    </button>
                  </div>
                )}

                {/* Settled state label */}
                {isSettled && status === "accepted" && (
                  <div className="crd-settled-tag crd-settled-tag--green">
                    <CheckCircle size={14} strokeWidth={2.5} />
                    Quote accepted — order placed
                  </div>
                )}
                {isSettled && status === "rejected" && req.quotedPrice && (
                  <div className="crd-settled-tag crd-settled-tag--red">
                    <XCircle size={14} strokeWidth={2.5} />
                    Quote declined by you
                  </div>
                )}
              </>
            )}
          </div>

          {/* Status Timeline */}
          <div className="crd-card">
            <div className="crd-card-head">
              <h2 className="crd-card-title">
                <Clock size={15} strokeWidth={2.2} /> Request Timeline
              </h2>
            </div>
            <div className="crd-timeline">
              {[
                {
                  label: "Request Submitted",
                  done: true,
                  date: new Date(req.created_at).toLocaleDateString("en-GB"),
                },
                { label: "Under Review", done: status !== "pending" },
                {
                  label: "Quote Sent",
                  done:
                    status === "quoted" ||
                    status === "accepted" ||
                    status === "rejected",
                },
                {
                  label: "Customer Response",
                  done:
                    status === "accepted" ||
                    (status === "rejected" && !!req.quotedPrice),
                },
                { label: "Order Placed", done: status === "accepted" },
              ].map((step, i, arr) => (
                <div
                  key={i}
                  className={`crd-step ${step.done ? "crd-step--done" : ""}`}
                >
                  <div className="crd-step-left">
                    <div className="crd-step-dot">
                      {step.done ? (
                        <CheckCircle size={11} strokeWidth={2.5} />
                      ) : (
                        <span className="crd-step-dot-inner" />
                      )}
                    </div>
                    {i < arr.length - 1 && <div className="crd-step-line" />}
                  </div>
                  <div className="crd-step-body">
                    <p className="crd-step-label">{step.label}</p>
                    {step.date && <p className="crd-step-date">{step.date}</p>}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Help */}
          <div className="crd-help-card">
            <AlertCircle size={15} strokeWidth={2} color="#9a7060" />
            <p>
              Have questions?{" "}
              <Link to="/customer/help-support" className="crd-help-link">
                Contact Support
              </Link>
            </p>
          </div>
        </div>
      </div>

      {/* Lightbox */}
      {lightboxIndex !== null && (
        <Lightbox
          images={imageUrls}
          startIndex={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
        />
      )}

      {/* Confirm Popup */}
      {confirmType && (
        <ConfirmPopup
          type={confirmType}
          quotedPrice={req.quotedPrice}
          onConfirm={handleConfirm}
          onCancel={() => setConfirmType(null)}
        />
      )}

      {/* Result Popup — reject path only; accept navigates straight to checkout */}
      {resultType && (
        <ResultPopup
          type={resultType}
          onClose={handleResultClose}
          onGoToOrders={() => navigate("/customer/orders")}
        />
      )}
    </div>
  );
};

export default CustomRequestDetail;
